"use client";

import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { AnimatePresence, motion } from "motion/react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import {
  type CreateProfileFormData,
  createProfileSchema,
} from "../../schemas/createProfileSchema.ts";
import { useProfileDataStore } from "../../store/useProfileDataStore.tsx";

import { toast } from "@/utils/customToast";
import { Button } from "@/components/ui/button.tsx";
import { Form } from "@/components/ui/form.tsx";
// import { ScrollArea } from "@/components/ui/scroll-area.tsx";
import { BasicInfoStep } from "@/features/profile/tutor/components/create-profile/steps/BasicInfo.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import GetStarted from "@/features/profile/tutor/components/create-profile/steps/GetStarted.tsx";
import AddNewLanguage from "@/features/profile/tutor/components/create-profile/steps/AddNewLanguage.tsx";
import { SelectProfileLanguage } from "@/features/profile/tutor/components/create-profile/steps/SelectProfileLanguage.tsx";
import { SelectLanguages } from "@/features/profile/tutor/components/create-profile/steps/SelectLanguages.tsx";
import { Stepper } from "@/features/profile/tutor/components/Stepper.tsx";
import { SelectSubjects } from "@/features/profile/tutor/components/create-profile/steps/SelectSubjects.tsx";
import { ProfileSummary } from "@/features/profile/tutor/components/create-profile/steps/ProfileSummary.tsx";
import { axiosApiInstance } from "@/libs/axios.tsx";
import { API_ENDPOINTS } from "@/constants/api.ts";
import PATHS from "@/routes/paths.ts";
import availableLanguages from "@/i18n/availableLanguages.ts";
import { Education } from "@/features/profile/tutor/components/create-profile/steps/Education.tsx";
import { Certificates } from "@/features/profile/tutor/components/create-profile/steps/Certificates.tsx";

const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
};
const pageTransition = {
  type: "tween",
  duration: 0.3,
};
const buttonVariants = {
  initial: {
    opacity: 0,
    y: 20,
  },
  in: {
    opacity: 1,
    y: 0,
  },
  out: {
    opacity: 0,
    y: -20,
  },
};
const buttonTransition = {
  type: "tween",
  duration: 0.2,
};
// Circle positions (no absolute top-left usage). Mirrors ProfileForm pattern while avoiding TL.
const circlePositions: { circle1: string; circle2: string }[] = [
  // 0: use TR / BL instead of TL / BR
  { circle1: "-top-40 -right-40", circle2: "-bottom-40 -left-40" },
  // 1: TR / BL
  { circle1: "-top-40 -right-40", circle2: "-bottom-40 -left-40" },
  // 2: BR / TR (avoid TL)
  { circle1: "-bottom-40 -right-40", circle2: "-top-40 -right-40" },
  // 3: BL / BR (avoid TL)
  { circle1: "-bottom-40 -left-40", circle2: "-bottom-40 -right-40" },
  // 4: use TR / BL for smaller radius
  { circle1: "-top-32 -right-32", circle2: "-bottom-32 -left-32" },
  // 5: BR / TR (avoid TL)
  { circle1: "-top-32 -right-32", circle2: "-bottom-32 -right-32" },
  // 6: BL / TR (avoid TL)
  { circle1: "-bottom-32 -left-32", circle2: "-top-32 -right-32" },
  // 7: repeat TR / BL as default alignment before summary override
  { circle1: "-top-32 -right-32", circle2: "-bottom-32 -left-32" },
];

export function CreateProfileForm() {
  const { t } = useTranslation(["profile", "common", "forms", "help"]);
  const [currentStep, setCurrentStep] = useState(0);
  const [navButtonsDisabled, setNavButtonsDisabled] = useState(false);
  const [isError, setIsError] = useState(false);
  const { user } = useUserStore();
  const redirect = useNavigate();
  const location = useLocation();
  const isNewLanguageProfile = location.pathname === PATHS.TUTOR.CREATE_PROFILE_NL;

  const form = useForm<CreateProfileFormData>({
    resolver: zodResolver(createProfileSchema(t)),
    defaultValues: {
      name: `${user?.firstName} ${user?.lastName}`,
      languages: [],
    },
    mode: "onChange",
  });

  const validateStep = (step: number) => {
    const stepSchema = steps[step].schema;

    try {
      stepSchema.parse(form.getValues());
      setIsError(false);

      return true;
    } catch (error: any) {
      error.errors.forEach((err: any) => {
        if (err.path[0] !== "current_location_coords") {
          toast.error(err.message);
        }
      });
      form.trigger();
      setIsError(true);

      return false;
    }
  };

  const { control } = form;

  const formValues = useWatch({ control });
  const { setHasProfile, hasProfile, settingsData, setToReload } = useProfileDataStore();

  useEffect(() => {
    form.resetField("languages");
    form.resetField("subjects");
  }, [formValues.locale_code, form.resetField]);

  const handleSubmit = async () => {
    try {
      const data = {
        ...formValues,
        // @ts-ignore
        education: formValues?.education?.length > 0 ? formValues.education : null,
        //@ts-ignore
        certificates: formValues?.certificates?.length > 0 ? formValues.certificates : null,
      };

      await axiosApiInstance.post<{ code: string; message: string }>(
        hasProfile
          ? API_ENDPOINTS.TUTOR.PROFILE.CREATE_PROFILE_NL
          : API_ENDPOINTS.TUTOR.PROFILE.CREATE_PROFILE,
        data,
      );
      setHasProfile(undefined);
      setToReload(true);
      if (user?.specialRoleChecks?.has_default_pricing) {
        redirect(`${PATHS.TUTOR.PUBLIC_PROFILE}/${formValues.locale_code}`);
      } else {
        redirect(`${PATHS.TUTOR.SCHEDULE.PRICING}`);
      }
    } catch (error) {
      console.log(error);

      return error;
    }
  };

  const goToNextStep = () => {
    const valid = validateStep(currentStep);

    if (currentStep < steps.length - 1 && valid) {
      setCurrentStep((prevState) => prevState + 1);
      console.log(formValues);
      form.clearErrors();
    }
  };

  const steps = [
    {
      icon: <Icon icon={"mdi:human-welcome"} />,
      component: isNewLanguageProfile ? (
        <AddNewLanguage goToNextStep={goToNextStep} />
      ) : (
        <GetStarted goToNextStep={goToNextStep} />
      ),
      schema: z.object({}),
    },
    {
      icon: <Icon icon={"mdi:globe"} />,
      component: <SelectProfileLanguage control={control} goToNextStep={goToNextStep} />,
      schema: createProfileSchema(t).pick({ locale_code: true }),
    },
    {
      icon: <Icon icon={"mdi:identification-card"} />,
      component: <BasicInfoStep control={control} />,
      schema: createProfileSchema(t).pick({
        headline: true,
        youtube_url: true,
        name: true,
      }),
    },
    {
      icon: <Icon icon={"mdi:language"} />,
      component: (
        <SelectLanguages control={control} setNavButtonsDisabled={setNavButtonsDisabled} />
      ),
      schema: createProfileSchema(t).pick({ languages: true }),
    },
    {
      icon: <Icon icon={"mdi:bookshelf"} />,
      component: <SelectSubjects control={control} setNavButtonsDisabled={setNavButtonsDisabled} />,
      schema: createProfileSchema(t).pick({ subjects: true }),
    },
    {
      icon: <Icon icon={"mdi:account-student"} />,
      component: <Education control={control} setNavButtonsDisabled={setNavButtonsDisabled} />,
      schema: createProfileSchema(t).pick({ education: true }),
    },
    {
      icon: <Icon icon={"mdi:certificate"} />,
      component: <Certificates control={control} setNavButtonsDisabled={setNavButtonsDisabled} />,
      schema: createProfileSchema(t).pick({ certificates: true }),
    },

    {
      icon: <Icon icon={"mdi:account-check"} />,
      component: (
        <ProfileSummary handleSubmit={handleSubmit} isNewLanguageProfile={isNewLanguageProfile} />
      ),
      schema: z.object({}),
    },
  ];

  const Step = steps[currentStep];

  if (settingsData?.main_profile.profiles_locales.length === availableLanguages.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center">
        <motion.h1
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 text-2xl font-semibold"
          initial={{ opacity: 0, y: 20 }}
        >
          {t("profile:createProfile.allLanguagesComplete")}
        </motion.h1>
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          initial={{ opacity: 0, y: 20 }}
          transition={{ delay: 0.2 }}
        >
          <Button size="lg" variant="ghost" onClick={() => redirect(PATHS.TUTOR.PUBLIC_PROFILE)}>
            {t("profile:createProfile.backToProfile")}
          </Button>
        </motion.div>
      </div>
    );
  }

  // Tips removed for create profile main layout per updated design (use full-width content after step 0)

  return (
    <Form {...form}>
      <motion.form
        animate={{ opacity: 1 }}
        className="flex h-full w-full items-center justify-center"
        initial={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        onSubmit={(e) => e.preventDefault()}
      >
        {/* Stable background circles */}
        <motion.div
          layout
          animate={{ opacity: 0.25 }}
          className={`absolute ${
            currentStep === steps.length - 1
              ? "top-1/5 size-full xl:top-1/2 xl:left-1/3 xl:size-320 xl:-translate-x-1/4 xl:-translate-y-1/2"
              : circlePositions[currentStep].circle1
          } size-120 rounded-full ${
            isError
              ? "bg-red-600! opacity-40!"
              : "bg-gradient-to-br from-blue-500/20 to-cyan-500/20"
          } opacity-30`}
          initial={{ opacity: 0 }}
          layoutId="circle1"
          transition={{ layout: { duration: 0.5 } }}
        />
        <motion.div
          layout
          animate={{ opacity: 0.25 }}
          className={`absolute ${
            currentStep === steps.length - 1
              ? "bottom-1/5 size-full xl:top-1/2 xl:right-1/3 xl:size-240 xl:translate-x-1/4 xl:-translate-y-1/2"
              : circlePositions[currentStep].circle2
          } size-120 rounded-full ${
            isError
              ? "bg-red-600! opacity-40!"
              : "bg-gradient-to-br from-purple-500/20 to-pink-500/20"
          } opacity-30`}
          initial={{ opacity: 0.2 }}
          layoutId="circle2"
          transition={{ layout: { duration: 0.5 } }}
        />

        {/* Foreground content */}
        <div className="z-10 flex h-full w-full max-w-7xl flex-col justify-center max-sm:justify-start">
          {/* Top horizontal stepper */}
          <div className="pointer-events-none absolute top-0 right-0 left-0 z-50">
            <div className="py-2">
              <div className="hidden xl:block">
                <div className="pointer-events-auto mx-auto flex w-full max-w-7xl justify-center">
                  <Stepper currentStep={currentStep} orientation="horizontal" steps={steps} />
                </div>
              </div>
              {/* Hide stepper on small screens */}
            </div>
          </div>

          {/* Main grid */}
          <div className="grid grid-cols-1 gap-6 p-2 sm:p-4 lg:grid-cols-1">
            {/* Step content centered and full width */}
            <div className="m-auto w-full max-w-4xl">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  animate="in"
                  transition={pageTransition}
                  variants={pageVariants}
                >
                  {Step.component}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>

          {/* Navigation buttons */}
          <AnimatePresence>
            {currentStep > 0 && (
              <motion.div
                animate="in"
                className="absolute bottom-3 left-5 sm:bottom-5"
                exit="out"
                initial="initial"
                transition={buttonTransition}
                variants={buttonVariants}
              >
                <Button
                  disabled={navButtonsDisabled}
                  type="button"
                  variant="defaultAccent"
                  onClick={() => setCurrentStep((prev) => (prev - 1 >= 0 ? prev - 1 : prev))}
                >
                  <Icon icon={"mdi:arrow-left"} />
                  {t("common:goBack")}
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {currentStep < steps.length - 1 && currentStep > 0 && (
              <motion.div
                animate="in"
                className="absolute right-5 bottom-3 sm:bottom-5"
                exit="out"
                initial="initial"
                transition={buttonTransition}
                variants={buttonVariants}
              >
                <Button
                  disabled={navButtonsDisabled}
                  type="button"
                  variant="foreground"
                  onClick={goToNextStep}
                >
                  {t("common:next")}
                  <Icon icon={"mdi:arrow-right"} />
                </Button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.form>
    </Form>
  );
}
